import { useCallback, useEffect } from 'react';
import { Button, Snackbar, Alert } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import '@fontsource/rubik';
import { updateState } from '../redux/commonSlice';
import usePharoahVisibility from '../pages/dashboard/hooks/usePharoahVisibility';

/**
 * Undo affordance for the last dashboard hide/show action. Rendered above the board
 * (not inside the card) because hiding removes the card from the list immediately.
 * Undo calls the same endpoint with the flag flipped.
 */

// commonReducer is persisted, so an entry can survive a reload — an undo offered for
// an action taken in a previous session would be surprising and, worse, act on stale
// state. Anything older than the toast's own lifetime is dropped.
const UNDO_TTL_MS = 15000;

const PharoahVisibilityUndoSnackbar = () => {
  const dispatch = useDispatch();
  const undo = useSelector((state) => state.root.commonReducer?.pharoah_visibility_undo);
  const { toggleVisibility, isToggling } = usePharoahVisibility();

  const close = useCallback(() => dispatch(updateState({ pharoah_visibility_undo: null })), [dispatch]);

  const isFresh = !!undo?.event_id && Date.now() - (undo?.at ?? 0) < UNDO_TTL_MS;

  useEffect(() => {
    if (undo?.event_id && !isFresh) close();
  }, [undo?.event_id, isFresh, close]);

  const handleUndo = () => {
    if (!undo?.event_id) return;
    toggleVisibility(
      { eventId: undo.event_id, hidden: !undo.hidden, statusTypeOrder: undo.status_type_order },
      { onSuccess: close }
    );
  };

  return (
    <Snackbar
      open={isFresh}
      autoHideDuration={8000}
      onClose={(_, reason) => {
        if (reason === 'clickaway') return;
        close();
      }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert
        icon={false}
        severity="info"
        onClose={close}
        sx={{
          backgroundColor: '#08231B',
          color: '#FFFFFF',
          fontFamily: 'Rubik',
          fontSize: { xs: '10px', md: '11px', xl: '13px' },
          alignItems: 'center',
          border: '1px solid #707070',
        }}
        action={
          <Button
            size="small"
            onClick={handleUndo}
            disabled={isToggling === undo?.event_id}
            sx={{
              fontFamily: 'Rubik',
              fontWeight: 700,
              fontSize: { xs: '10px', md: '11px', xl: '13px' },
              color: '#21D644',
              textTransform: 'none',
            }}
          >
            Undo
          </Button>
        }
      >
        {undo?.hidden
          ? `"${undo?.name ?? 'Project'}" is hidden from the dashboard`
          : `"${undo?.name ?? 'Project'}" is visible on the dashboard`}
      </Alert>
    </Snackbar>
  );
};

export default PharoahVisibilityUndoSnackbar;
